import { EntityDetailSkeleton } from '@/components/layout/entity-detail-skeleton'
import { useEffect, useState } from 'react'

import { OrganisationPublicProfileStack } from '@/components/public/organisation-public-profile-content'
import { PublicPageHead } from '@/components/public/public-page-head'
import { PublicShareLayout } from '@/components/public/public-share-layout'
import { PUBLIC_CARD_SURFACE } from '@/components/public/public-surfaces'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { API_ORIGIN } from '@/constants'
import { usePublicShareRouteParam } from '@/lib/public-share-route-param'
import type { PublicOrganisation } from '@/types/public-share'

export function PublicOrganisationPage({ slugOverride }: { slugOverride?: string } = {}) {
  const slug = usePublicShareRouteParam('/o/:slug', 'slug', slugOverride)
  const [organisation, setOrganisation] = useState<PublicOrganisation | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!slug) {
        if (!cancelled) {
          setLoading(false)
          setError('Organisation not found')
        }
        return
      }
      setLoading(true)
      setError(null)
      try {
        const response = await fetch(
          `${API_ORIGIN}/api/public/organisations/${encodeURIComponent(slug.trim())}?include_jobs=true`,
        )
        const data = await response.json()
        if (!response.ok) throw new Error(data.error || 'Organisation not found')
        if (!cancelled) setOrganisation(data as PublicOrganisation)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Organisation not found')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [slug])

  if (loading) {
    return (
      <PublicShareLayout>
        <EntityDetailSkeleton
          variant="profile"
          withPageContainer={false}
          includeJobsSection
          ariaLabel="Loading organisation"
        />
      </PublicShareLayout>
    )
  }

  if (error || !organisation) {
    return (
      <PublicShareLayout>
        <Card className={PUBLIC_CARD_SURFACE}>
          <CardHeader>
            <CardTitle>Organisation unavailable</CardTitle>
            <CardDescription alwaysShow>{error || 'This organisation could not be loaded.'}</CardDescription>
          </CardHeader>
        </Card>
      </PublicShareLayout>
    )
  }

  return (
    <PublicShareLayout>
      <PublicPageHead
        title={organisation.name}
        description={organisation.description?.slice(0, 200) || organisation.industry}
        imageUrl={organisation.logo_url}
        canonicalPath={`/o/${organisation.slug}`}
      />
      <OrganisationPublicProfileStack organisation={organisation} />
    </PublicShareLayout>
  )
}
